import React from 'react';

import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';
import { cssMap } from '@atlaskit/css';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { TOOLBAR_RANK, TOOLBARS, VIEW_MODE_TOGGLE_SECTION } from '@atlaskit/editor-common/toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { ReadonlyTransaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { RegisterComponent } from '@atlaskit/editor-toolbar-model';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';

import type {
	MarkdownModePlugin,
	MarkdownModePluginOptions,
	MarkdownModePluginState,
	MarkdownModeView,
} from './markdownModePluginType';
import { buildGlobalKeydownHandler } from './pm-plugins/keymap';
import { markdownModePluginKey } from './pm-plugins/main';
import {
	blockquoteSourceViewCommand,
	boldSourceViewCommand,
	bulletListSourceViewCommand,
	heading1SourceViewCommand,
	heading2SourceViewCommand,
	heading3SourceViewCommand,
	heading4SourceViewCommand,
	heading5SourceViewCommand,
	heading6SourceViewCommand,
	inlineCodeSourceViewCommand,
	italicSourceViewCommand,
	orderedListSourceViewCommand,
	strikeSourceViewCommand,
	taskListSourceViewCommand,
	insertSourceTableCommand,
} from './source-view-utils';
import type {
	SourceViewCommand,
	SourceViewFormatState,
	SourceViewPositionState,
} from './source-view-utils';
import { MarkdownModeContentComponentDiff } from './ui/MarkdownModeContentComponentDiff';
import { MarkdownModeViewToggle } from './ui/MarkdownModeViewToggle';

const HEADING_SOURCE_VIEW_COMMANDS: Record<1 | 2 | 3 | 4 | 5 | 6, SourceViewCommand> = {
	1: heading1SourceViewCommand,
	2: heading2SourceViewCommand,
	3: heading3SourceViewCommand,
	4: heading4SourceViewCommand,
	5: heading5SourceViewCommand,
	6: heading6SourceViewCommand,
};

type MarkdownModeApi = ExtractInjectionAPI<MarkdownModePlugin> | undefined;

// Pinning rule: any non-preview view (syntax / split-view) hides the PM editor,
// so a floating selection toolbar has nothing to anchor to.
const contextualFormattingOverrideForView = (
	view: MarkdownModeView,
	isLivePage?: boolean,
): 'always-pinned' | 'controlled' => {
	return view === 'preview' && isLivePage ? 'controlled' : 'always-pinned';
};

// Mirrors `selection-toolbar/PrimaryToolbarComponent`: prefers
// `userPreferences.toolbarDockingPosition` when the new plugin is enabled,
// otherwise falls back to `selectionToolbar.toolbarDocking`. In source /
// preview view the primary toolbar is forcibly pinned via the toolbar
// override (see `setView` below), so we treat the toolbar as docked-to-top
// regardless of the user's saved preference — otherwise the view-switcher
// renders in-content while the toolbar is actually pinned at the top.
const useIsToolbarDockedToTop = (api: MarkdownModeApi): boolean => {
	const { dockingPositionFromPrefs, isLivePage, view } = useSharedPluginStateWithSelector(
		api,
		['userPreferences', 'selectionToolbar', 'markdownMode'],
		(states) => ({
			dockingPositionFromPrefs: states.userPreferencesState?.preferences?.toolbarDockingPosition,
			isLivePage: states.markdownModeState?.isLivePage,
			view: states.markdownModeState?.view,
		}),
	);
	if (view && (view !== 'preview' || !isLivePage)) {
		return true;
	}
	return dockingPositionFromPrefs === 'top';
};

const MarkdownModeContentComponentDiffWrapper = ({
	editorView,
	config,
	api,
}: {
	api: MarkdownModeApi;
	config: MarkdownModePluginOptions;
	editorView: EditorView;
}) => {
	const hasReconciledPersistedViewRef = React.useRef(false);
	// Transient fallback: if the shared state hasn't hydrated yet, treat the
	// view as 'preview' so we don't briefly mount the syntax editor over the
	// PM editor. The authoritative initial view (which can be 'syntax') is
	// resolved upstream via `config.viewModePersistence.getInitialView` and
	// will populate `markdownModeState.view` once the plugin initialises.
	const currentView = useSharedPluginStateWithSelector(
		api,
		['markdownMode'],
		(states) => states.markdownModeState?.view ?? ('preview' as MarkdownModeView),
	);
	const showSourceLineNumbers = useSharedPluginStateWithSelector(
		api,
		['markdownMode'],
		(states) => states.markdownModeState?.showSourceLineNumbers ?? true,
	);
	const wrapSourceCode = useSharedPluginStateWithSelector(
		api,
		['markdownMode'],
		(states) => states.markdownModeState?.wrapSourceCode ?? true,
	);

	React.useEffect(() => {
		if (hasReconciledPersistedViewRef.current) {
			return;
		}
		hasReconciledPersistedViewRef.current = true;

		const persistedView = config.viewModePersistence?.getInitialView?.();
		if (!persistedView || persistedView === currentView) {
			return;
		}

		api?.markdownMode?.actions.setView(persistedView);
	}, [api, config.viewModePersistence, currentView]);

	return (
		<>
			<MarkdownModeContentComponentDiff
				editorView={editorView}
				config={config}
				currentView={currentView}
				showSourceLineNumbers={showSourceLineNumbers}
				wrapSourceCode={wrapSourceCode}
				api={api}
			/>
			<InContentToggle api={api} />
		</>
	);
};

const styles = cssMap({
	trailingSection: {
		position: 'absolute',
		insetInlineEnd: token('space.150'),
		insetBlockStart: '50%',
		transform: 'translateY(-50%)',
		display: 'flex',
		alignItems: 'center',
	},
	// `pointer-events: none` so the row never intercepts clicks on the editor surface beneath it.
	inContentToggleStickyRow: {
		position: 'fixed',
		insetBlockStart: token('space.100'),
		insetInlineEnd: token('space.300'),
		zIndex: layers.card(),
		pointerEvents: 'none',
	},
	inContentToggleAnchor: {
		display: 'flex',
		alignItems: 'center',
		paddingBlock: token('space.050'),
		paddingInline: token('space.050'),
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		pointerEvents: 'auto',
	},
});

const InToolbarToggle = ({ api }: { api: MarkdownModeApi }) => {
	const isDockedToTop = useIsToolbarDockedToTop(api);
	if (!isDockedToTop) {
		return null;
	}
	return (
		<Box xcss={styles.trailingSection} testId="markdown-mode-view-toggle-section">
			<MarkdownModeViewToggle api={api} />
		</Box>
	);
};

export const InContentToggle = ({ api }: { api: MarkdownModeApi }): React.ReactElement | null => {
	const isDockedToTop = useIsToolbarDockedToTop(api);
	if (isDockedToTop) {
		return null;
	}
	return (
		<Box xcss={styles.inContentToggleStickyRow}>
			<Box xcss={styles.inContentToggleAnchor} testId="markdown-mode-view-toggle-in-content">
				<MarkdownModeViewToggle api={api} />
			</Box>
		</Box>
	);
};

const buildToolbarComponents = (
	config: MarkdownModePluginOptions | undefined,
	api: MarkdownModeApi,
): RegisterComponent[] => {
	if (!config?.isMarkdownMode) {
		return [];
	}

	return [
		{
			type: VIEW_MODE_TOGGLE_SECTION.type,
			key: VIEW_MODE_TOGGLE_SECTION.key,
			parents: [
				{
					type: 'toolbar',
					key: TOOLBARS.PRIMARY_TOOLBAR,
					rank: TOOLBAR_RANK[VIEW_MODE_TOGGLE_SECTION.key],
				},
			],
			component: () => <InToolbarToggle api={api} />,
		},
	];
};

export const markdownModePlugin: MarkdownModePlugin = ({ config, api }) => {
	let editorView: EditorView | undefined;
	let sourceCommandDispatch: ((cmd: SourceViewCommand) => boolean) | null = null;

	const dispatchSource = (cmd: SourceViewCommand): boolean => {
		if (!fg('platform_editor_markdown_compatible_toolbar')) {
			return false;
		}
		if (!sourceCommandDispatch) {
			return false;
		}
		return sourceCommandDispatch(cmd);
	};
	const setEditorView = (newEditorView: EditorView) => {
		editorView = newEditorView;
	};
	const dispatchSourceFormatState = (formatState: SourceViewFormatState | null) => {
		if (!editorView) {
			return;
		}
		editorView.dispatch(
			editorView.state.tr.setMeta(markdownModePluginKey, {
				sourceBlockFormatState: formatState?.block ?? null,
				sourceInlineFormatState: formatState?.inline ?? null,
				sourceListFormatState: formatState?.list ?? null,
			}),
		);
	};
	const dispatchSourcePositionState = (positionState: SourceViewPositionState | null) => {
		if (!editorView) {
			return;
		}
		editorView.dispatch(
			editorView.state.tr.setMeta(markdownModePluginKey, {
				sourcePositionState: positionState,
			}),
		);
	};
	const dispatchShowSourceLineNumbers = (showSourceLineNumbers: boolean) => {
		if (!editorView) {
			return;
		}
		editorView.dispatch(
			editorView.state.tr.setMeta(markdownModePluginKey, {
				showSourceLineNumbers,
			}),
		);
	};
	const dispatchWrapSourceCode = (wrapSourceCode: boolean) => {
		if (!editorView) {
			return;
		}
		editorView.dispatch(
			editorView.state.tr.setMeta(markdownModePluginKey, {
				wrapSourceCode,
			}),
		);
	};

	// Resolve the initial view ONCE, at plugin construction time — not in
	// `init()`, which re-runs on `state.reconfigure` and would clobber any
	// in-flight user choice on every preset rebuild.
	//
	// When the initial view is browser-persisted, SSR cannot know the stored
	// value. Keep SSR and the first hydrating client render on the deterministic
	// default, then reconcile the persisted value from the content component's
	// mount effect.
	const initialView: MarkdownModeView = 'syntax';

	api?.toolbar?.actions.registerComponents(buildToolbarComponents(config, api));

	return {
		name: 'markdownMode',

		getSharedState(editorState) {
			if (!editorState) {
				return {
					view: initialView,
					isLivePage: config?.isLivePage ?? false,
					isMarkdownMode: config?.isMarkdownMode ?? false,
					showSourceLineNumbers: true,
					wrapSourceCode: true,
					sourceInlineFormatState: null,
					sourceBlockFormatState: null,
					sourceListFormatState: null,
					sourcePositionState: null,
				};
			}
			return (
				markdownModePluginKey.getState(editorState) ?? {
					view: initialView,
					isLivePage: config?.isLivePage ?? false,
					isMarkdownMode: config?.isMarkdownMode ?? false,
					showSourceLineNumbers: true,
					wrapSourceCode: true,
					sourceInlineFormatState: null,
					sourceBlockFormatState: null,
					sourceListFormatState: null,
					sourcePositionState: null,
				}
			);
		},

		actions: {
			setView(view: MarkdownModeView) {
				if (!editorView) {
					return;
				}
				const prevView = markdownModePluginKey.getState(editorView.state)?.view ?? initialView;
				if (prevView === view) {
					return;
				}
				const tr = editorView.state.tr;
				api?.toolbar?.commands.setContextualFormattingModeOverride(
					contextualFormattingOverrideForView(view, config?.isLivePage),
				)({ tr });
				tr.setMeta(markdownModePluginKey, { view });
				editorView.dispatch(tr);
				config?.viewModePersistence?.onViewChange?.(view, prevView);
			},

			dispatchSourceCommand: dispatchSource,
			insertSourceTable: (rowsCount?: number, colsCount?: number) =>
				dispatchSource(insertSourceTableCommand(rowsCount, colsCount)),

			toggleSourceBlockquote: () => dispatchSource(blockquoteSourceViewCommand),
			toggleSourceBulletList: () => dispatchSource(bulletListSourceViewCommand),
			toggleSourceOrderedList: () => dispatchSource(orderedListSourceViewCommand),
			toggleSourceTaskList: () => dispatchSource(taskListSourceViewCommand),
			toggleSourceHeading: (level: 1 | 2 | 3 | 4 | 5 | 6) =>
				dispatchSource(HEADING_SOURCE_VIEW_COMMANDS[level]),
			toggleSourceBold: () => dispatchSource(boldSourceViewCommand),
			toggleSourceItalic: () => dispatchSource(italicSourceViewCommand),
			toggleSourceInlineCode: () => dispatchSource(inlineCodeSourceViewCommand),
			toggleSourceStrike: () => dispatchSource(strikeSourceViewCommand),

			registerSourceCommandDispatch(dispatcher: ((cmd: SourceViewCommand) => boolean) | null) {
				if (!fg('platform_editor_markdown_compatible_toolbar')) {
					return;
				}
				sourceCommandDispatch = dispatcher;
			},

			setSourceBlockFormatState(formatState: MarkdownModePluginState['sourceBlockFormatState']) {
				if (!fg('platform_editor_markdown_compatible_toolbar') || !editorView) {
					return;
				}
				editorView.dispatch(
					editorView.state.tr.setMeta(markdownModePluginKey, {
						sourceBlockFormatState: formatState,
					}),
				);
			},

			setSourceFormatState(formatState) {
				if (!fg('platform_editor_markdown_compatible_toolbar')) {
					return;
				}
				dispatchSourceFormatState(formatState);
			},

			setSourceInlineFormatState(formatState: MarkdownModePluginState['sourceInlineFormatState']) {
				if (!fg('platform_editor_markdown_compatible_toolbar') || !editorView) {
					return;
				}
				editorView.dispatch(
					editorView.state.tr.setMeta(markdownModePluginKey, {
						sourceInlineFormatState: formatState,
					}),
				);
			},

			setSourceListFormatState(formatState: MarkdownModePluginState['sourceListFormatState']) {
				if (!fg('platform_editor_markdown_compatible_toolbar') || !editorView) {
					return;
				}
				editorView.dispatch(
					editorView.state.tr.setMeta(markdownModePluginKey, {
						sourceListFormatState: formatState,
					}),
				);
			},

			setSourcePositionState(positionState) {
				dispatchSourcePositionState(positionState);
			},

			setShowSourceLineNumbers(showSourceLineNumbers) {
				dispatchShowSourceLineNumbers(showSourceLineNumbers);
			},

			setWrapSourceCode(wrapSourceCode) {
				dispatchWrapSourceCode(wrapSourceCode);
			},
		},

		contentComponent({ editorView: contentEditorView }) {
			if (!config?.isMarkdownMode || !contentEditorView) {
				return null;
			}
			return (
				<MarkdownModeContentComponentDiffWrapper
					editorView={contentEditorView}
					config={config}
					api={api}
				/>
			);
		},

		pmPlugins() {
			return [
				{
					name: 'markdownModeState',
					plugin: () => {
						return new SafePlugin<MarkdownModePluginState>({
							key: markdownModePluginKey,
							state: {
								init: () => ({
									view: initialView,
									isLivePage: config?.isLivePage ?? false,
									isMarkdownMode: config?.isMarkdownMode ?? false,
									showSourceLineNumbers: true,
									wrapSourceCode: true,
									sourceInlineFormatState: null,
									sourceBlockFormatState: null,
									sourceListFormatState: null,
									sourcePositionState: null,
								}),
								apply(tr: ReadonlyTransaction, currentState: MarkdownModePluginState) {
									const meta = tr.getMeta(markdownModePluginKey) as
										| {
												showSourceLineNumbers?: MarkdownModePluginState['showSourceLineNumbers'];
												sourceBlockFormatState?: MarkdownModePluginState['sourceBlockFormatState'];
												sourceInlineFormatState?: MarkdownModePluginState['sourceInlineFormatState'];
												sourceListFormatState?: MarkdownModePluginState['sourceListFormatState'];
												sourcePositionState?: MarkdownModePluginState['sourcePositionState'];
												view?: MarkdownModeView;
												wrapSourceCode?: MarkdownModePluginState['wrapSourceCode'];
										  }
										| undefined;
									if (meta) {
										return {
											...currentState,
											...(meta.view !== undefined ? { view: meta.view } : {}),
											...(meta.showSourceLineNumbers !== undefined
												? { showSourceLineNumbers: meta.showSourceLineNumbers }
												: {}),
											...(meta.wrapSourceCode !== undefined
												? { wrapSourceCode: meta.wrapSourceCode }
												: {}),
											...(meta.sourceInlineFormatState !== undefined
												? { sourceInlineFormatState: meta.sourceInlineFormatState }
												: {}),
											...(meta.sourceBlockFormatState !== undefined
												? { sourceBlockFormatState: meta.sourceBlockFormatState }
												: {}),
											...(meta.sourceListFormatState !== undefined
												? { sourceListFormatState: meta.sourceListFormatState }
												: {}),
											...(meta.sourcePositionState !== undefined
												? { sourcePositionState: meta.sourcePositionState }
												: {}),
										};
									}
									return currentState;
								},
							},
							view(newEditorView: EditorView) {
								setEditorView(newEditorView);
								let destroyed = false;

								// Seed the toolbar override on first mount so a page that
								// opens straight into source/preview pins the toolbar without
								// needing a user-triggered `setView`.
								if (config?.isMarkdownMode) {
									// Deferred to a microtask: dispatching synchronously inside
									// `view()` is a PM re-entry hazard — the meta can be dropped
									// before downstream plugins' `apply()` runs. By the next
									// microtask the editor is fully constructed and dispatch is safe.
									// The `destroyed` guard prevents dispatching on a torn-down
									// view if `destroy()` runs before the microtask fires (e.g.
									// React StrictMode double-mount).
									Promise.resolve().then(() => {
										if (destroyed) {
											return;
										}
										const tr = newEditorView.state.tr;
										// Read the latest markdown view from plugin state at microtask
										// time. During page transitions, relying on the static SSR
										// default (`initialView`) can briefly seed the wrong override.
										const viewForOverride =
											markdownModePluginKey.getState(newEditorView.state)?.view ?? initialView;
										api?.toolbar?.commands.setContextualFormattingModeOverride(
											contextualFormattingOverrideForView(viewForOverride, config?.isLivePage),
										)({ tr });
										newEditorView.dispatch(tr);
									});
								}

								// Register a global keydown listener for the toggle shortcut.
								// We use document-level rather than a PM keymap because in
								// source view the PM editor is hidden/blurred and PM keymaps
								// never fire.
								const handler = buildGlobalKeydownHandler(api, () => editorView);
								const doc = getDocument();
								const unbind = doc ? bind(doc, { type: 'keydown', listener: handler }) : () => {};

								return {
									destroy() {
										destroyed = true;
										unbind();
									},
								};
							},
						});
					},
				},
			];
		},
	};
};
